import { useEffect, useMemo, useRef, useState } from 'react';
import { type VideoJsPlayer, type VideoJsPlayerOptions } from 'video.js';

import styles from './Livestream.module.scss';
import StreamStatusOverlay, { type StreamOverlayStatus } from './StreamStatusOverlay.js';
import VideoJs from './VideoJs.js';
import { type StreamData, StreamQuality, useCheckStreamExists, useStartStream, useStreamData } from '../api/index.js';
import useStreamStatus from '../hooks/websocket-hooks/useStreamStatus.js';


type Props = {
  cameraId: string;
  isPlaying: boolean;
  isHighlighted?: boolean;
  onClick?: () => void;
  isHQ: boolean;
};

const RETRY_BASE_DELAY_MS = 15000;
const RETRY_MAX_DELAY_MS = 60000;
const MANUAL_RETRY_THROTTLE_MS = 5000;
const RECONNECT_ERROR_AFTER_MS = 30000;

const LivestreamHls = (props: Props) => {
  const { cameraId, isPlaying, isHighlighted = false, isHQ } = props;
  const [isHQVideoReady, setIsHQVideoReady] = useState<boolean>(false);
  const [errorCount, setErrorCount] = useState<number>(0);
  const [retryCycle, setRetryCycle] = useState<number>(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const lastRetryAtRef = useRef<number>(0);
  // starts stream depending on quality and whenever the quality changes.
  // this also serves as a check to see of the stream is running.
  const hasStreamStarted = useStartStream(cameraId, isHQ ? StreamQuality.HIGH : StreamQuality.LOW, {
    enabled: Boolean(cameraId),
  });

  // Contains streamIds and Tokens of different quality streams.
  const lowQualityStreamData = useStreamData(cameraId, StreamQuality.LOW, {
    enabled: Boolean(cameraId),
  });
  const highQualityStreamData = useStreamData(cameraId, StreamQuality.HIGH, {
    enabled: Boolean(cameraId),
  });

  // these verify if the stream is giving streaming data or not.
  // Automatically re-fetches whenever the StreamData changes.
  // this should happen when the Quality changes (isHQ).
  const isLowQualityStreamAvailable = useCheckStreamExists(lowQualityStreamData.data ?? ({} as StreamData), {
    enabled: Boolean(lowQualityStreamData.data) && !isHQ,
    staleTime: 1000 * 60 * 5,
  });
  const isHighQualityStreamAvailable = useCheckStreamExists(highQualityStreamData.data ?? ({} as StreamData), {
    enabled: Boolean(highQualityStreamData.data) && isHQ,
    staleTime: 1000 * 60 * 5,
  });

  const streamIds = useMemo(
    () =>
      [lowQualityStreamData.data?.streamId, highQualityStreamData.data?.streamId]
        .filter((id): id is string => Boolean(id)),
    [lowQualityStreamData.data?.streamId, highQualityStreamData.data?.streamId],
  );
  const streamStatusById = useStreamStatus(streamIds);
  const activeStreamId = isHQ ? highQualityStreamData.data?.streamId : lowQualityStreamData.data?.streamId;
  const activeStreamStatus = activeStreamId ? streamStatusById.get(activeStreamId) : undefined;
  // With alwaysAvailable the server plays filler through a camera drop, so the
  // player never errors — the WS push is the only reconnect signal for MediaMTX
  // streams; the errorCount path stays as the fallback for Ant Media streams.
  // retryCycle keeps the state through the errorCount reset between backoff cycles.
  const playerErrorActive = errorCount >= 3 || retryCycle > 0;
  const isReconnecting = activeStreamStatus?.status === 'reconnecting' || playerErrorActive;
  const [reconnectingSince, setReconnectingSince] = useState<number | null>(null);
  const [showReconnectError, setShowReconnectError] = useState<boolean>(false);

  useEffect(() => {
    if (!isReconnecting) {
      setReconnectingSince(null);
      setShowReconnectError(false);
      return;
    }
    const wsSince = activeStreamStatus?.since ? Date.parse(activeStreamStatus.since) : NaN;
    setReconnectingSince((current) => current ?? (Number.isNaN(wsSince) ? Date.now() : wsSince));
  }, [isReconnecting, activeStreamStatus?.since]);

  useEffect(() => {
    if (reconnectingSince === null) {
      return;
    }
    const remaining = Math.max(0, reconnectingSince + RECONNECT_ERROR_AFTER_MS - Date.now());
    const timer = setTimeout(() => setShowReconnectError(true), remaining);
    return () => clearTimeout(timer);
  }, [reconnectingSince]);

  useEffect(() => {
    if (isHQVideoReady !== isHQ) {
      // start the stream or just make sure it is on.
      hasStreamStarted.refetch();
      // these re-fetches cause the VideoJs components to re-render
      if (isHQ) {
        highQualityStreamData.refetch();
      } else {
        lowQualityStreamData.refetch();
      }
    } else {
      // this is required in case someone toggles the isHQ very fast
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    }
  }, [isHQ]);

  // recorded video (VOD)
  const isVod = useMemo<boolean>(() => {
    if (lowQualityStreamData.data && highQualityStreamData.data) {
      return lowQualityStreamData.data.streamId === highQualityStreamData.data.streamId;
    }
    return false;
  }, [lowQualityStreamData, highQualityStreamData]);

  const refetchStream = (streamId?: string): void => {
    hasStreamStarted.refetch();
    if (streamId) {
      if (streamId.includes('high')) {
        highQualityStreamData.refetch();
      } else {
        lowQualityStreamData.refetch();
      }
    } else {
      highQualityStreamData.refetch();
      lowQualityStreamData.refetch();
    }
  };

  const runRetry = (streamId?: string): void => {
    lastRetryAtRef.current = Date.now();
    refetchStream(streamId);
  };

  // auto-recover from a media-server blip: exponential backoff up to one attempt
  // per minute, never giving up — a stranded tile on the monitoring wall is worse
  // than one light request a minute.
  useEffect(() => {
    if (errorCount < 3) {
      return;
    }
    const delay = Math.min(RETRY_BASE_DELAY_MS * 2 ** retryCycle, RETRY_MAX_DELAY_MS);
    const timer = setTimeout(() => {
      setRetryCycle((cycle) => cycle + 1);
      setErrorCount(0);
      runRetry();
    }, delay);
    timerRef.current = timer;
    return () => clearTimeout(timer);
  }, [errorCount, retryCycle]);

  // The server telling us the source is back is the fastest recovery signal for
  // an errored player — retry immediately instead of waiting out the backoff.
  useEffect(() => {
    if (activeStreamStatus?.status === 'online' && playerErrorActive) {
      setErrorCount(0);
      setRetryCycle(0);
      runRetry();
    }
  }, [activeStreamStatus?.status, playerErrorActive]);

  const onError = (_: MediaError | null, streamId: string): void => {
    // upon 5 retries, display error message.
    if (errorCount >= 3) {
      return;
    }
    // increment tries
    setErrorCount(errorCount + 1);
    // attempt to restart stream
    refetchStream(streamId);
  };

  const onRetry = () => {
    if (Date.now() - lastRetryAtRef.current < MANUAL_RETRY_THROTTLE_MS) {
      return;
    }
    setErrorCount(0);
    setRetryCycle(0);
    runRetry();
  };

  // facilitates smooth changes between stream qualities
  const onReady = (_: VideoJsPlayer, streamId: string) => {
    setErrorCount(0);
    setRetryCycle(0);
    setIsHQVideoReady(streamId.includes('high'));
  };

  const mainStyleClasses = useMemo(() => {
    let result = `${styles['livestream']} ${styles['livestream--clickable']}`;
    if (isHighlighted) result += ` ${styles['livestream--highlighted']}`;
    if (isPlaying) result += ` ${styles['livestream--playing']}`;
    return result;
  }, [isHighlighted, isPlaying]);

  const isActiveStreamAvailable = isHQ ? isHighQualityStreamAvailable.data : isLowQualityStreamAvailable.data;
  const canShowLowQuality =
    Boolean(lowQualityStreamData.data) && Boolean(isLowQualityStreamAvailable.data) && (!isHQ || !isHQVideoReady) && !isVod;
  const canShowHighQuality =
    Boolean(highQualityStreamData.data) && Boolean(isHighQualityStreamAvailable.data) && (isHQ || isHQVideoReady);
  const overlayStatus = useMemo((): StreamOverlayStatus | null => {
    if (!isPlaying) {
      return null;
    }
    if (isReconnecting) {
      return showReconnectError ? 'error' : 'reconnecting';
    }
    return isActiveStreamAvailable || canShowLowQuality || canShowHighQuality ? null : 'initializing';
  }, [isPlaying, isReconnecting, showReconnectError, isActiveStreamAvailable, canShowLowQuality, canShowHighQuality]);

  const videoJsOptions: VideoJsPlayerOptions = {
    liveui: true,
    liveTracker: {
      trackingThreshold: 0,
    },
    html5: {
      vhs: {
        limitRenditionByPlayerDimensions: false,
      },
    },
    autoplay: 'muted',
  };

  return (
    <div
      className={mainStyleClasses}
      onClick={() => {
        if (overlayStatus === 'error') return onRetry();
        if (props.onClick) {
          props.onClick();
        }
      }}
    >
      {overlayStatus ? (
        <StreamStatusOverlay status={overlayStatus} errorMessage={activeStreamStatus?.lastError} />
      ) : null}

      {isPlaying ? (
        <>
          <div className={styles['livestream__video']}>
            {lowQualityStreamData.data &&
              canShowLowQuality && (
                <VideoJs
                  key={lowQualityStreamData.data.token}
                  options={videoJsOptions}
                  token={lowQualityStreamData.data.token}
                  streamId={lowQualityStreamData.data.streamId}
                  live={!isVod}
                  style={{ zIndex: 1 }}
                  onReady={onReady}
                  onError={onError}
                  onStreamStop={refetchStream}
                  src={isVod ? lowQualityStreamData.data.vodUrl : lowQualityStreamData.data.hlsUrl}
                />
              )}
            {highQualityStreamData.data &&
              canShowHighQuality && (
                <VideoJs
                  key={highQualityStreamData.data.token}
                  options={videoJsOptions}
                  token={highQualityStreamData.data.token}
                  streamId={highQualityStreamData.data.streamId}
                  style={{ zIndex: isHQVideoReady ? 2 : 0 }}
                  onReady={onReady}
                  onError={onError}
                  onStreamStop={refetchStream}
                  live={!isVod}
                  src={isVod ? highQualityStreamData.data.vodUrl : highQualityStreamData.data.hlsUrl}
                />
              )}
          </div>
          <div className={styles['livestream__video_overlay']}></div>
        </>
      ) : (
        <div className={styles['livestream__video']}></div>
      )}
    </div>
  );
};

export default LivestreamHls;
